import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces';
import { TOYAMA_LOGO_BASE64 } from './toyama-logo';

type PdfMakeLike = {
	vfs?: Record<string, string>;
	createPdf: (docDefinition: TDocumentDefinitions) => { download: (filename?: string, cb?: () => void, errCb?: (err: unknown) => void) => void };
};

type EngravingKey = { key_index?: number; key_number?: number; name?: string | null };
type EngravingKeypad = { id: number; address?: number; name?: string; location_room?: number };
type EngravingData = {
	project: { name: string };
	unit?: { name: string };
	keypads: EngravingKeypad[];
	keysByKeypad: Map<number, EngravingKey[]>;
	roomsById: Map<number, string>;
};

async function loadPdfMake(): Promise<PdfMakeLike> {
	if (typeof window !== 'undefined' && (window as { pdfMake?: PdfMakeLike }).pdfMake) return (window as { pdfMake: PdfMakeLike }).pdfMake;
	const pdfMakeModule = await import('pdfmake/build/pdfmake');
	const fontsModule = await import('pdfmake/build/vfs_fonts');
	const pdfMake = (pdfMakeModule.default || pdfMakeModule) as PdfMakeLike;
	const fonts = (fontsModule.default || fontsModule) as { pdfMake?: { vfs?: Record<string, string> }; vfs?: Record<string, string> };
	pdfMake.vfs = fonts.pdfMake?.vfs || fonts.vfs || pdfMake.vfs;
	if (typeof window !== 'undefined') (window as { pdfMake?: PdfMakeLike }).pdfMake = pdfMake;
	return pdfMake;
}

export async function generateEngravingSheetPdf(data: EngravingData, filename: string): Promise<void> {
	const pdfMake = await loadPdfMake();
	const rows: string[][] = [];
	for (const kp of data.keypads) {
		const keys = data.keysByKeypad.get(kp.id) ?? [];
		const room = data.roomsById.get(kp.location_room ?? -1) ?? '—';
		if (!keys.length) {
			rows.push([kp.name ?? `Keypad ${kp.id}`, String(kp.address ?? '—'), room, '—', '(no keys)']);
			continue;
		}
		for (const key of keys.sort((a, b) => (a.key_index ?? a.key_number ?? 0) - (b.key_index ?? b.key_number ?? 0))) {
			rows.push([kp.name ?? `Keypad ${kp.id}`, String(kp.address ?? '—'), room, String((key.key_index ?? key.key_number ?? 0) + 1), key.name?.trim() ? key.name : '(blank)']);
		}
	}
	const headerStack: Content[] = [
		{ text: 'Engraving Sheet', style: 'title' },
		{ text: data.project.name, style: 'subtitle' },
		...(data.unit ? [{ text: data.unit.name, style: 'muted' } satisfies Content] : [])
	];
	const doc: TDocumentDefinitions = {
		pageSize: 'A4',
		pageMargins: [40, 60, 40, 40],
		content: [
			{
				columns: [
					{ image: TOYAMA_LOGO_BASE64, width: 80 },
					{ stack: headerStack, margin: [12, 0, 0, 0] }
				],
				margin: [0, 0, 0, 16]
			},
			{
				table: {
					headerRows: 1,
					widths: ['*', 50, '*', 40, '*'],
					body: [['Keypad', 'Addr', 'Room', 'Btn', 'Engraving'], ...rows]
				},
				layout: 'lightHorizontalLines'
			}
		],
		styles: { title: { fontSize: 16, bold: true, color: '#10223A' }, subtitle: { fontSize: 11, color: '#226A73', margin: [0, 4, 0, 0] }, muted: { fontSize: 9, color: '#64748b' } },
		defaultStyle: { fontSize: 9 }
	};
	return new Promise((resolve, reject) => {
		pdfMake.createPdf(doc).download(filename, () => resolve(), reject);
	});
}

export function engravingSheetCSV(data: EngravingData): string {
	const lines = ['keypad,address,room,button,engraving'];
	for (const kp of data.keypads) {
		const keys = data.keysByKeypad.get(kp.id) ?? [];
		const room = data.roomsById.get(kp.location_room ?? -1) ?? '';
		if (!keys.length) {
			lines.push(csvRow([kp.name ?? `Keypad ${kp.id}`, kp.address ?? '', room, '', '']));
			continue;
		}
		for (const key of keys.sort((a, b) => (a.key_index ?? 0) - (b.key_index ?? 0))) {
			lines.push(csvRow([kp.name ?? `Keypad ${kp.id}`, kp.address ?? '', room, (key.key_index ?? 0) + 1, key.name?.trim() ?? '']));
		}
	}
	return lines.join('\n');
}

function csvRow(cells: Array<string | number>): string {
	return cells
		.map((c) => {
			const s = String(c ?? '');
			return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
		})
		.join(',');
}

export function downloadTextFile(content: string, filename: string, mime = 'text/csv'): void {
	const blob = new Blob([content], { type: mime });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

export function addressScheduleCSV(
	rows: Array<{ device_type: string; room: string; branch: number | string; address: number | string; model: string }>
): string {
	const lines = ['device_type,room,branch,address,model'];
	for (const row of rows) lines.push(csvRow([row.device_type, row.room, row.branch, row.address, row.model]));
	return lines.join('\n');
}
